import { useState, useRef, useEffect, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import {
  Bot, X, Send, Trash2, Copy, Check, Loader2, ChevronDown, Sparkles,
  TrendingUp, AlertTriangle, FileText, LayoutDashboard, ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";

type Role = "user" | "assistant";

interface Message {
  id: string;
  role: Role;
  content: string;
  timestamp: Date;
  /** If set, shows an "Apply Suggestion" nav button after this AI message */
  suggestedAction?: { label: string; path: string };
}

export interface AiSuggestion {
  title: string;
  description: string;
  action: string;
}

interface AiAssistantPanelProps {
  suggestions?: AiSuggestion[];
  onNavigate?: (path: string) => void;
}

async function sendChatMessage(
  message: string,
  history: { role: Role; content: string }[],
): Promise<string> {
  const res = await fetch("/api/admin/ai/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ message, history }),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? "Failed to get response");
  }
  const data = (await res.json()) as { response: string };
  return data.response;
}

/** Prompt text for each quick-action type */
const ACTION_PROMPTS: Record<string, string> = {
  prioritize_pools:
    "Which pools should I prioritize for payout this week and why? Consider balance, number of people waiting, wait times, and category urgency.",
  create_payout:
    "Help me decide which debts to include in the next payout batch. Which pool should I pay first and what would maximize impact?",
  draft_letter:
    "Draft a creditor payment letter I can use when sending a payout. Include placeholders for creditor name, amount, and reference number.",
  view_summary:
    "Give me a complete summary of today's platform state — pools, debts waiting, recent donations, and the top 2–3 things I should act on today.",
};

/**
 * When AI responds to this action type, offer an "Apply Suggestion" shortcut.
 * For payout-related actions, navigate with ?autoOpen=true so the create dialog
 * opens automatically on arrival at the target page.
 * Keys match ACTION_PROMPTS keys.
 */
const ACTION_NAV: Record<string, { label: string; path: string }> = {
  prioritize_pools: { label: "Open Payout Batches", path: "/admin/payout-batches?autoOpen=true" },
  create_payout: { label: "Create Payout Batch", path: "/admin/payout-batches?autoOpen=true" },
};

const ACTION_ICONS: Record<string, typeof Bot> = {
  prioritize_pools: TrendingUp,
  create_payout: LayoutDashboard,
  draft_letter: FileText,
  view_summary: Sparkles,
  review_flagged: AlertTriangle,
};

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={copy}
      className="p-1 rounded hover:bg-muted opacity-60 hover:opacity-100 transition-opacity shrink-0"
      title="Copy to clipboard"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

export function AiAssistantPanel({ suggestions, onNavigate }: AiAssistantPanelProps) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    if (open) setTimeout(() => textareaRef.current?.focus(), 100);
  }, [open]);

  const sendMessage = useCallback(
    async (messageText: string, actionType?: string) => {
      const text = messageText.trim();
      if (!text || loading) return;

      const userMsg: Message = {
        id: crypto.randomUUID(),
        role: "user",
        content: text,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMsg]);
      setInput("");
      setLoading(true);

      try {
        const history = messages
          .slice(-20)
          .map((m) => ({ role: m.role, content: m.content }));
        const response = await sendChatMessage(text, history);

        // If this message was triggered by a known action type, attach a nav shortcut
        const suggestedAction = actionType ? ACTION_NAV[actionType] : undefined;

        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: "assistant",
            content: response,
            timestamp: new Date(),
            suggestedAction,
          },
        ]);
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : "Unknown error";
        toast({ title: "AI Error", description: errMsg, variant: "destructive" });
      } finally {
        setLoading(false);
      }
    },
    [loading, messages, toast],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void sendMessage(input);
    }
  };

  return (
    <>
      {/* Floating toggle button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-3 shadow-lg hover:bg-primary/90 transition-all"
        aria-label="AI Assistant"
      >
        <Bot className="h-5 w-5" />
        <span className="text-sm font-medium hidden sm:inline">AI Assistant</span>
        {open && <ChevronDown className="h-4 w-4" />}
      </button>

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-20 right-4 sm:right-6 z-50 w-[min(calc(100vw-2rem),440px)] flex flex-col rounded-2xl shadow-2xl border bg-background overflow-hidden"
          style={{ height: "min(600px, calc(100vh - 120px))" }}
        >
          {/* Header */}
          <div className="flex items-center gap-2 px-4 py-3 bg-primary text-primary-foreground shrink-0">
            <Bot className="h-5 w-5" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">DebtBridge AI Assistant</div>
              <div className="text-xs opacity-75">Live platform context</div>
            </div>
            <button
              onClick={() => { setMessages([]); setInput(""); }}
              title="Clear chat"
              className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
            >
              <Trash2 className="h-4 w-4" />
            </button>
            <button
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.length === 0 && (
              <div className="space-y-3">
                <div className="text-center py-3">
                  <Bot className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm font-medium">Ready to help</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Ask me about pools, debts, or request a draft.
                  </p>
                </div>

                {/* Daily suggestions fetched on dashboard load */}
                {suggestions && suggestions.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground px-1">Suggested for today</p>
                    {suggestions.map((s, i) => {
                      const Icon = ACTION_ICONS[s.action] ?? Sparkles;
                      const navAction = ACTION_NAV[s.action];
                      return (
                        <div
                          key={i}
                          className="rounded-xl border p-3 space-y-2 hover:bg-muted/40 transition-colors"
                        >
                          <div className="flex items-start gap-2.5">
                            <Icon className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium">{s.title}</div>
                              <div className="text-xs text-muted-foreground mt-0.5">{s.description}</div>
                            </div>
                          </div>
                          <div className="flex gap-1.5 pl-6">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-xs px-2"
                              onClick={() => void sendMessage(ACTION_PROMPTS[s.action] ?? s.title, s.action)}
                            >
                              Ask AI
                            </Button>
                            {navAction && onNavigate && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs px-2 gap-1"
                                onClick={() => { onNavigate(navAction.path); setOpen(false); }}
                              >
                                Apply Suggestion
                                <ArrowRight className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Quick actions */}
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-muted-foreground px-1">Quick actions</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {(
                      [
                        { label: "Prioritize pools", action: "prioritize_pools", icon: TrendingUp },
                        { label: "Plan payout", action: "create_payout", icon: LayoutDashboard },
                        { label: "Draft letter", action: "draft_letter", icon: FileText },
                        { label: "Today's summary", action: "view_summary", icon: Sparkles },
                      ] as const
                    ).map(({ label, action, icon: Icon }) => (
                      <Button
                        key={action}
                        variant="outline"
                        size="sm"
                        className="h-auto py-2 px-2 text-xs justify-start gap-1.5"
                        onClick={() => void sendMessage(ACTION_PROMPTS[action] ?? label, action)}
                      >
                        <Icon className="h-3.5 w-3.5 shrink-0" />
                        {label}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {messages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                {msg.role === "user" ? (
                  <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-primary text-primary-foreground px-3.5 py-2.5 text-sm">
                    {msg.content}
                  </div>
                ) : (
                  <div className="max-w-[95%] w-full rounded-2xl rounded-tl-sm border bg-muted/30 px-3.5 py-2.5 text-sm space-y-2">
                    <div className="flex items-center gap-1.5">
                      <Bot className="h-3.5 w-3.5 text-primary shrink-0" />
                      <Badge variant="secondary" className="text-xs px-1.5 py-0 h-4">AI</Badge>
                      <CopyButton text={msg.content} />
                    </div>
                    <div className="prose prose-sm max-w-none dark:prose-invert [&>p]:leading-relaxed [&>ul]:my-1 [&>ol]:my-1 [&>h1]:text-base [&>h2]:text-sm [&>h3]:text-sm">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                    {/* Apply Suggestion — contextual nav shown when AI responds to action prompts */}
                    {msg.suggestedAction && onNavigate && (
                      <div className="pt-1 border-t">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs px-2.5 gap-1.5"
                          onClick={() => { onNavigate(msg.suggestedAction!.path); setOpen(false); }}
                        >
                          Apply Suggestion: {msg.suggestedAction.label}
                          <ArrowRight className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="rounded-2xl rounded-tl-sm border bg-muted/30 px-3.5 py-2.5 flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  <span className="text-sm text-muted-foreground">Thinking…</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t p-3 shrink-0 bg-background">
            <div className="flex gap-2 items-end">
              <Textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask anything about pools, debts, or request a draft…"
                className="resize-none min-h-[40px] max-h-[120px] text-sm"
                rows={1}
                disabled={loading}
              />
              <Button
                size="icon"
                className="shrink-0 h-10 w-10"
                disabled={!input.trim() || loading}
                onClick={() => void sendMessage(input)}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1.5 px-0.5">
              Enter to send · Shift+Enter for new line · AI cannot take actions
            </p>
          </div>
        </div>
      )}
    </>
  );
}
